import toast from "react-hot-toast";
import { CurrentUser } from "../Page";
import React, { useEffect, useState } from "react";
import {
  OrderCarouselQuery,
  OrderSessionCreateInput,
  RecipeDocument,
  RecipeQuery,
  RecipeQueryVariables,
  useCreateManyOrdersMutation,
} from "@/queries/autogenerate/graphql";
import {
  dot,
  getNumberOfStudents,
  searchClasses,
  shapeClass,
  shapeClassLinear,
  ShapedClass,
} from "@/lib/class-utilities";
import AsyncSelect from "react-select/async";
import { useClient } from "urql";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { classNames, isAdmin, isAssistant, isTeacher } from "@/lib/util";
import Tooltip from "../styles/Tooltip";
import mixpanel from "mixpanel-browser";
import { eventEmitter } from "@/lib/events";
import cuid from "cuid";

interface BulkOrderProps {
  user: CurrentUser;
  selectedRecipes: OrderCarouselQuery["recipes"];
  classes: NonNullable<CurrentUser["school"]>["classes"];
}

export const BulkOrder: React.FC<BulkOrderProps> = ({
  user,
  selectedRecipes,
  classes,
}) => {
  const client = useClient();
  const [{ fetching, error }, createManyOrders] = useCreateManyOrdersMutation();

  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (needsConfirmation) {
      setTimeout(() => {
        setNeedsConfirmation(false);
      }, 5 * 1000);
    }
  }, [needsConfirmation]);

  const [defaultClass, setDefaultClass] = useState<ShapedClass | null>(null);

  return (
    <section className="mb-5">
      <h4 className="font-normal">Bulk Order</h4>

      <div className="m-2 space-y-2 rounded border border-cyan-200 bg-cyan-50 p-2">
        <small className="block">
          For recipes with a class assigned, the order will be for servings
          matching the number of students in the class.
        </small>
        <small className="block">
          For recipes without a class assigned, you will need to select a class
          to make orders for.
        </small>
      </div>

      {selectedRecipes.some((r) => !r.classes.length) ? (
        <div className="m-2">
          <p>
            Some of the selected recipes do not have a class assigned. Please
            select a class to order for in these cases.
          </p>

          <AsyncSelect
            className="radio-select"
            classNamePrefix="react-select"
            isSearchable={true}
            value={defaultClass}
            defaultOptions
            loadOptions={(inputValue, callback) => {
              callback([...searchClasses(inputValue, classes.map(shapeClass))]);
            }}
            noOptionsMessage={() => "No Options"}
            maxMenuHeight={200}
            placeholder="Select a class..."
            onChange={(data) => {
              if (data?.value) {
                setDefaultClass(shapeClassLinear(data.obj));
              }
            }}
            styles={{
              container: (provided) => ({
                ...provided,
                position: "sticky",
                top: "0px",
                marginTop: "0",
              }),
              control: (styles) => ({
                ...styles,
                backgroundColor: "white",
              }),
              option: (styles, { data }) =>
                data.type === "shaped-class"
                  ? {
                      ...styles,
                      ...dot(...(data?.colors || [])),
                    }
                  : { ...styles },
              input: (styles) => ({
                ...styles,
                // height: "35px",
                ...dot(),
              }),
              placeholder: (styles) => ({ ...styles, ...dot() }),
              singleValue: (provided, { data }) => ({
                ...provided,
                ...dot(...(data?.colors || [])),
              }),
            }}
          />
        </div>
      ) : null}

      {selectedRecipes.some((r) => !r.classes.length) &&
      !defaultClass ? null : needsConfirmation ? (
        <Tooltip
          key={0}
          content={
            <span>
              <h2 className="mb-3 font-medium">Confirm</h2>
              <p className="font-medium">
                Click again to confirm the one-click order.
              </p>
              {/* <p className="font-normal">
            Caution: clicking this button can result in many orders being made.
            Please make sure you have your ducks in a row before continuing.
          </p> */}
            </span>
          }
          placement="bottom"
          visible
        >
          <button
            disabled={
              (!isAssistant(user) && !isAdmin(user)) || preparing || fetching
            }
            className="btn"
            onClick={async () => {
              setPreparing(true);

              toast(`Preparing ${selectedRecipes.length} orders`);

              const rawData: (OrderSessionCreateInput | null)[] =
                await Promise.all(
                  selectedRecipes.map<Promise<OrderSessionCreateInput | null>>(
                    async (initialRecipe) => {
                      // we need the full recipe for this
                      const { data } = await client
                        .query<RecipeQuery, RecipeQueryVariables>(
                          RecipeDocument,
                          {
                            id: initialRecipe.id,
                          }
                        )
                        .toPromise();
                      const recipe = data?.recipe;
                      if (!recipe) return null;

                      const classe =
                        classes.find((c) => c.id === recipe.classes[0]?.id) ??
                        defaultClass?.obj;

                      if (!classe) return null;
                      const servings = getNumberOfStudents(classe);

                      return {
                        date: new Date(),
                        school: { connect: { id: user.school?.id } },
                        user: {
                          connect: {
                            id:
                              classe.users.find((u) => isTeacher(u))?.id ||
                              user.id,
                          },
                        },
                        classe: { connect: { id: classe.id } },
                        // lesson: { connect: { id: lesson.id } },
                        orders: {
                          create: recipe.recipeIngredients.map(
                            (recipeIngredient) => ({
                              id: cuid(),
                              amount: servings * recipeIngredient.amount,
                              ingredient: {
                                connect: {
                                  id: recipeIngredient.ingredient.id,
                                },
                              },
                              school: { connect: { id: user.school?.id } },
                              user: {
                                connect: {
                                  id:
                                    classe.users.find((u) => isTeacher(u))
                                      ?.id || user.id,
                                },
                              },
                              classe: { connect: { id: classe.id } },
                              unit: {
                                connect: {
                                  id: recipeIngredient.unit.id,
                                },
                              },
                            })
                          ),
                        },
                        recipe: { connect: { id: recipe.id } },
                        servings: servings,
                      };
                    }
                  )
                );

              const data = rawData.filter(
                (d) => d !== null
              ) as OrderSessionCreateInput[];

              setPreparing(false);

              await createManyOrders({
                data,
              }).then(async ({ error }) => {
                if (error) {
                  toast.error("There was an error creating the orders.");
                  console.error(error);
                } else {
                  if (selectedRecipes.length) {
                    toast.success("The orders have been made");
                    eventEmitter.emit("made-order", true);
                  }

                  setNeedsConfirmation(false);

                  mixpanel.track("One-click ordered");
                  mixpanel.track("Made orders", {
                    scheduled: false,
                    lesson: false,
                    period: true,
                  });
                }
              });
            }}
          >
            {preparing || fetching ? (
              <div className="spinner h-6 w-6"></div>
            ) : (
              <ShoppingCartIcon
                className={classNames(
                  !isAssistant(user) && !isAdmin(user)
                    ? "cursor-not-allowed text-gray-500"
                    : "text-green-600 hover:text-green-800",
                  "w-6 transition"
                )}
              />
            )}
            Order {selectedRecipes.length} Selected Recipes
          </button>
        </Tooltip>
      ) : (
        <Tooltip
          key={1}
          content={
            <span>
              {!isAssistant(user) && !isAdmin(user) ? (
                <p className="mb-5 font-medium">
                  (Feature only available for Admins & Assistants)
                </p>
              ) : null}
              <p className="mb-5 font-medium">
                Quickly make an order for the selected recipes.
              </p>
              <p>
                Caution: clicking this button can result in many orders being
                made. Please make sure you have your ducks in a row before
                continuing.
              </p>
            </span>
          }
          placement="bottom"
        >
          <button
            className="btn"
            onClick={() => {
              setNeedsConfirmation(true);
            }}
            disabled={
              (!isAssistant(user) && !isAdmin(user)) || preparing || fetching
            }
          >
            {preparing || fetching ? (
              <div className="spinner h-6 w-6"></div>
            ) : (
              <ShoppingCartIcon
                className={classNames(
                  !isAssistant(user) && !isAdmin(user)
                    ? "cursor-not-allowed text-gray-500"
                    : "text-green-600 hover:text-green-800",
                  "w-6 transition"
                )}
              />
            )}
            Order {selectedRecipes.length} Selected Recipes
          </button>
        </Tooltip>
      )}
    </section>
  );
};
